import React from 'react';
import { useWatch } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';
import type { SkillLibraryCreate } from '../../generated/api/types.gen.js';
import { zSkillLibraryCreate } from '../../generated/api/zod.gen.js';
import { createToolLibraryMutation, listToolLibrariesQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import { Button, Modal, useToast } from '../../components/ui/index.js';
import { Form, SelectFormField, SubmitButton, TextFormField, useZodForm } from '../../components/form/index.js';

interface ToolLibraryModalProps {
  open: boolean;
  onClose: () => void;
}

const SOURCE_OPTIONS = [
  { value: 'git', label: 'Git repository', description: 'Tools are read from a branch of a repository' },
  { value: 'url', label: 'Manifest address', description: 'Tools are listed in a manifest served over HTTP' },
  { value: 'local', label: 'Local folder', description: 'A folder on the server' },
];

const schema = zSkillLibraryCreate.pick({
  name: true,
  description: true,
  sourceType: true,
  repoUrl: true,
  localPath: true,
  branch: true,
  subdirectory: true,
});

/** Add a library of tools (SKILL.md files) that agents can use. */
export const ToolLibraryModal: React.FC<ToolLibraryModalProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const create = useMutation(createToolLibraryMutation());
  const form = useZodForm(schema, {
    defaultValues: {
      name: '',
      description: '',
      sourceType: 'git',
      repoUrl: '',
      localPath: '',
      branch: 'main',
      subdirectory: '',
    },
  });
  const sourceType = useWatch({ control: form.control, name: 'sourceType' });

  const close = () => {
    form.reset();
    onClose();
  };

  const submit = async (values: SkillLibraryCreate) => {
    await create.mutateAsync({ body: values });
    await queryClient.invalidateQueries({ queryKey: listToolLibrariesQueryKey() });
    notify('Library added');
    close();
  };

  return (
    <Modal open={open} onClose={close} title="Add tool library" titleIcon={<BookOpen size={16} />}>
      <Form form={form} onSubmit={submit}>
        <TextFormField name="name" label="Name" placeholder="Anthropic official tools" required />
        <TextFormField name="description" label="Description" />
        <SelectFormField name="sourceType" label="Source" options={SOURCE_OPTIONS} required />
        {sourceType === 'local' ? (
          <TextFormField name="localPath" label="Folder path" placeholder="/srv/tools" />
        ) : (
          <TextFormField
            name="repoUrl"
            label={sourceType === 'git' ? 'Repository address' : 'Manifest address'}
            type="url"
            placeholder={
              sourceType === 'git' ? 'https://github.com/org/repo.git' : 'https://example.com/tools/manifest.json'
            }
          />
        )}
        {sourceType === 'git' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextFormField name="branch" label="Branch" />
            <TextFormField name="subdirectory" label="Subfolder" description="Optional." />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancel
          </Button>
          <SubmitButton>Add library</SubmitButton>
        </div>
      </Form>
    </Modal>
  );
};
